import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';

// Supported languages configuration
export const supportedLanguages = [
  { code: 'ar', name: 'العربية', flag: 'SA' },
  { code: 'bn', name: 'বাংলা', flag: 'BD' },
  { code: 'de', name: 'Deutsch', flag: 'DE' },
  { code: 'en', name: 'English', flag: 'GB' },
  { code: 'es', name: 'Español', flag: 'ES' },
  { code: 'fil', name: 'Filipino', flag: 'PH' },
  { code: 'fr', name: 'Français', flag: 'FR' },
  { code: 'hi', name: 'हिन्दी', flag: 'IN' },
  { code: 'hr', name: 'Hrvatski', flag: 'HR' },
  { code: 'id', name: 'Bahasa Indonesia', flag: 'ID' },
  { code: 'it', name: 'Italiano', flag: 'IT' },
  { code: 'ka', name: 'ქართული', flag: 'GE' },
  { code: 'ja', name: '日本語', flag: 'JP' },
  { code: 'ko', name: '한국어', flag: 'KR' },
  { code: 'ms', name: 'Bahasa Melayu', flag: 'MY' },
  { code: 'pl', name: 'Polski', flag: 'PL' },
  { code: 'pt', name: 'Português', flag: 'BR' },
  { code: 'th', name: 'ไทย', flag: 'TH' },
  { code: 'tr', name: 'Türkçe', flag: 'TR' },
  { code: 'uk', name: 'Українська', flag: 'UA' },
  { code: 'vi', name: 'Tiếng Việt', flag: 'VN' },
  { code: 'zh', name: '中文', flag: 'CN' },
] as const;

export type LanguageCode = typeof supportedLanguages[number]['code'];

// Default namespace for shared strings
export const defaultNS = 'common';

// Core namespace (shared strings only)
export const coreNamespaces = ['common'] as const;

// Component-specific namespaces
export const componentNamespaces = [
  'DesktopHeader',
  'MobileHeader',
  'MobileBottomNav',
  'Login',
  'SettingsModal',
  'AboutModal',
  'DesktopCashLobby',
  'DesktopAofLobby',
  'DesktopCryptoLobby',
  'DesktopHomeLobby',
  'TurnActionControls',
  'PlayerSlot',
  'GameRoom',
  'RoomInformation',
  'BuyInModal',
  'Tournament',
  'Notifications',
  'WinTournamentModal',
  'Activity',
  'DesktopFooter',
  'Casino',
] as const;

// All namespaces to preload
export const allNamespaces = [...coreNamespaces, ...componentNamespaces] as const;
const translationAssetVersion = encodeURIComponent(
  import.meta.env.VITE_APP_BUILD_ID || import.meta.env.VITE_APP_VERSION || 'dev'
);

// Initialize i18n and export the promise for waiting
export const i18nInitPromise = i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    supportedLngs: supportedLanguages.map((language) => language.code),
    nonExplicitSupportedLngs: true,
    load: 'languageOnly',
    defaultNS,
    ns: [...allNamespaces],

    // Enable partial bundling for language switching
    partialBundledLanguages: true,

    debug: false,

    interpolation: {
      escapeValue: false,
    },

    // Backend configuration - supports per-component files
    backend: {
      loadPath: `${import.meta.env.BASE_URL}locales/{{lng}}/{{ns}}.json?v=${translationAssetVersion}`,
    },

    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },

    react: {
      useSuspense: true,
    },
  });

// Helper to change language
export const changeLanguage = (lng: LanguageCode): Promise<void> => {
  return new Promise((resolve, reject) => {
    i18n.changeLanguage(lng, (err) => {
      if (err) reject(err);
      else resolve();
    });
  });
};

// Get current language
export const getCurrentLanguage = (): LanguageCode => {
  return (i18n.language?.split('-')[0] as LanguageCode) || 'en';
};

// Load a namespace on demand (for lazy loading component translations)
export const loadNamespace = (ns: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (i18n.hasLoadedNamespace(ns)) {
      resolve();
      return;
    }
    i18n.loadNamespaces(ns, (err) => {
      if (err) reject(err);
      else resolve();
    });
  });
};

// Global translation function
// Usage: t('common:actions.cancel') or t('TurnActionControls:rejoinGame')
export const t = i18n.t.bind(i18n);

// Helper to create a component-scoped translator
// Usage: const ct = createComponentTranslator('TurnActionControls');
//        ct('rejoinGame') -> translates 'TurnActionControls:rejoinGame'
export const createComponentTranslator = (namespace: string) => {
  // Ensure namespace is loaded
  loadNamespace(namespace);
  return (key: string, options?: Record<string, unknown>) => i18n.t(`${namespace}:${key}`, options);
};

// Cross-window language synchronization
// When language changes in one window (main or detached), sync to all other windows
const LANGUAGE_STORAGE_KEY = 'i18nextLng';

// Listen for storage events from other windows/tabs
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === LANGUAGE_STORAGE_KEY && event.newValue) {
      const newLang = event.newValue.split('-')[0] as LanguageCode;
      // Only change if different from current language to avoid loops
      if (newLang !== i18n.language?.split('-')[0]) {
        i18n.changeLanguage(newLang);
      }
    }
  });
}

export default i18n;
